// Saved element CRUD extracted from OfferPageBuilderSimple.js lines 3621-3955
import { useCallback } from 'react';
import api from '../../../services/api';
import { ELEMENT_TYPES } from '../../../utils/offerElementTypes';

export const useSavedElements = ({
  elements,
  pages,
  setPages,
  setElements,
  pageSettings,
  currentPageIndex,
  paginationEnabled,
  currentElementName,
  setCurrentElementName,
  isUpdatingElement,
  setIsUpdatingElement,
  selectedSavedElementId,
  setSelectedSavedElementId,
  selectedBrand,
  selectedOffer,
  brands,
  offers,
  setSavedOfferElements,
  setFilteredSavedElements,
  setStatusTitle,
  setStatusMessage,
  setShowStatusAlert,
  setShowSaveElementDialog,
  setPendingTemplateData,
  setShowReplaceConfirm,
  setShowLoadTemplateAlert,
  setLoadTemplateError,
  setSaveType,
  setIsLoadingExistingContent,
  setSelectedBrand,
  setSelectedOffer,
  loadOffersForBrand,
  setForceRender,
}) => {
  const saveCurrentElement = async () => {
    if (!currentElementName.trim() || elements.length === 0) {
      console.log('❌ Save offer elements failed: Missing name or no elements');
      setStatusTitle('Missing Information');
      setStatusMessage('Please enter a name and ensure you have elements on the canvas');
      setShowStatusAlert(true);
      return;
    }

    // Check for video elements without thumbnails
    const videoElementsWithoutThumbnails = elements.filter(element =>
      element.type === ELEMENT_TYPES.VIDEO && element.videoFile && !element.thumbnailUrl
    );

    if (videoElementsWithoutThumbnails.length > 0) {
      console.log('❌ Save offer elements failed: Video elements missing thumbnails');
      setStatusTitle('Missing Thumbnails');
      setStatusMessage(`Cannot save: ${videoElementsWithoutThumbnails.length} video element(s) are missing required thumbnails. Please upload thumbnails for all video elements before saving.`);
      setShowStatusAlert(true);
      return;
    }

    console.log('💾 Saving:', {
      name: currentElementName,
      type: 'template',
      isUpdating: isUpdatingElement,
      elementId: selectedSavedElementId
    });

    try {
      // Determine which elements to save based on pagination mode
      let elementsToSave;
      let paginationData = null;

      if (paginationEnabled && pages.length > 0) {
        // Pagination mode - flatten all pages into a single elements array
        elementsToSave = pages.flatMap(page => page.elements);
        paginationData = {
          enabled: true,
          pages: pages.map(page => ({
            id: page.id,
            elementCount: page.elements.length
          })),
          currentPageIndex: currentPageIndex
        };
        console.log('💾 Saving with pagination:', {
          totalElements: elementsToSave.length,
          pagesCount: pages.length,
          paginationData,
        });
      } else {
        // Regular mode - use current elements
        elementsToSave = elements;
        paginationData = { enabled: false };
        console.log('💾 Saving without pagination:', {
          totalElements: elementsToSave.length,
          paginationEnabled,
          pagesLength: pages.length
        });
      }

      // Optimize data structure to prevent BSON size limit issues
      const optimizedElements = elementsToSave.map(element => {
        const optimizedElement = { ...element };

        // For video elements, store only essential data to prevent size issues
        if (element.type === ELEMENT_TYPES.VIDEO && element.videoFile) {
          optimizedElement.videoFile = element.videoFile;
          delete optimizedElement.videoBlob;
          delete optimizedElement.videoData;
        }

        // For image elements, ensure we're not storing large base64 data unnecessarily
        if (element.type === ELEMENT_TYPES.CROPPABLE_IMAGE || element.type === ELEMENT_TYPES.CIRCULAR_IMAGE) {
          if (element.croppedImage && element.croppedImage.length > 10000) {
            console.warn('Large cropped image detected, consider optimizing');
          }
        }

        return optimizedElement;
      });

      // Store only essential data in content field (as JSON string)
      const elementData = {
        name: currentElementName,
        type: 'template',
        content: JSON.stringify({
          elements: optimizedElements,
          pageSettings: pageSettings,
          elementsCount: optimizedElements.length,
          pagination: paginationData
        }),
        styles: {},
        properties: {
          isTemplate: true,
          elementsCount: optimizedElements.length,
          brandName: brands.find(b => b._id === selectedBrand)?.name || '',
          offerName: offers.find(o => o._id === selectedOffer)?.title || ''
        },
        description: `Saved offer elements with ${optimizedElements.length} elements`,
        brandId: selectedBrand || null,
        offerId: selectedOffer || null
      };

      // Debug: Check for video elements with aspectRatio
      optimizedElements.forEach((el, idx) => {
        if (el.type === 'video') {
          console.log(`🎥 [SAVE] Video element ${idx} styles:`, el.styles);
        }
      });

      console.log('📦 Data to save:', elementData);
      console.log('🔍 Debug - selectedBrand:', selectedBrand, 'selectedOffer:', selectedOffer);

      let response;
      if (isUpdatingElement && selectedSavedElementId) {
        console.log('🔄 Updating existing item:', selectedSavedElementId);
        response = await api.put(`/offer-elements/${selectedSavedElementId}`, elementData);
      } else {
        console.log('🆕 Creating new item');
        response = await api.post('/offer-elements', elementData);
      }

      console.log('✅ Save successful:', response.data);

      // Reload the saved elements list with current filters
      const reloadParams = {};
      if (selectedBrand) reloadParams.brandId = selectedBrand;
      if (selectedOffer) reloadParams.offerId = selectedOffer;
      const reloadResponse = await api.get('/offer-elements', { params: reloadParams });
      setFilteredSavedElements(reloadResponse.data);
      setSavedOfferElements(reloadResponse.data);

      // Show success message
      setStatusTitle('Success');
      setStatusMessage(isUpdatingElement ? 'Offer elements updated successfully!' : 'Offer elements saved successfully!');
      setShowStatusAlert(true);

      // Close dialog
      setShowSaveElementDialog(false);

      // Only reset state if we're not updating an existing element
      if (!isUpdatingElement) {
        setCurrentElementName('');
        setIsUpdatingElement(false);
        setSelectedSavedElementId(null);
        setSaveType('template'); // Reset to default
      }
    } catch (error) {
      console.error('❌ Error saving offer element:', error);
      console.error('Error details:', error.response?.data || error.message);

      // Handle specific error cases
      setStatusTitle('Error Saving');
      if (error.response?.status === 413) {
        const errorData = error.response.data;
        setStatusMessage(`Document too large: ${errorData.message}\n\nSize: ${Math.round(errorData.size / 1024 / 1024)}MB (max: ${Math.round(errorData.maxSize / 1024 / 1024)}MB)\n\nPlease reduce the number of elements or remove large media files.`);
      } else if (error.response?.data?.message) {
        setStatusMessage(`Error: ${error.response.data.message}`);
      } else {
        const errorMessage = error.response?.data?.error || error.message || 'Failed to save';
        setStatusMessage(`Error: ${errorMessage}`);
      }
      setShowStatusAlert(true);
    }
  };

  const loadSavedElement = (savedElement) => {
    // Set flag to prevent auto-pagination for existing content
    setIsLoadingExistingContent(true);

    // Set brand and offer if they exist in the saved element
    if (savedElement.brandId) {
      setSelectedBrand(savedElement.brandId);
      if (savedElement.offerId) {
        setSelectedOffer(savedElement.offerId);
        // Load offers for the brand if not already loaded
        if (!offers.find(o => o._id === savedElement.offerId)) {
          loadOffersForBrand(savedElement.brandId);
        }
      }
    }

    if (savedElement.type === 'template' && savedElement.properties?.isTemplate) {
      // Load entire template
      try {
        // Parse template data from content field (new optimized structure)
        let templateData;
        if (savedElement.content) {
          templateData = JSON.parse(savedElement.content);
        } else if (savedElement.properties?.templateData) {
          // Fallback to old structure for backward compatibility
          templateData = savedElement.properties.templateData;
        }

        if (templateData && templateData.elements) {
          // Store template data for the confirmation dialog
          setPendingTemplateData({
            elements: templateData.elements,
            name: savedElement.name,
            id: savedElement._id,
            pageSettings: templateData.pageSettings,
            pagination: templateData.pagination
          });
          setShowReplaceConfirm(true);
        }
      } catch (error) {
        console.error('Error loading offer elements:', error);
        setShowLoadTemplateAlert(true);
        setLoadTemplateError('Error loading offer elements. The data may be corrupted.');
      }
    } else {
      // Load individual element
      const newElement = {
        id: Date.now(),
        type: savedElement.type,
        content: savedElement.content,
        styles: savedElement.styles || {},
        properties: savedElement.properties || {}
      };

      if (paginationEnabled) {
        setPages(prevPages => {
          const safeIndex = Math.min(currentPageIndex, prevPages.length - 1);
          return prevPages.map((page, index) =>
            index === safeIndex
              ? { ...page, elements: [...page.elements, newElement] }
              : page
          );
        });
      }
      setElements(prev => [...prev, newElement]);

      // Set the loaded element for update functionality
      setSelectedSavedElementId(savedElement._id);
      setCurrentElementName(savedElement.name);
      setIsUpdatingElement(true);
    }
  };

  const deleteSavedElement = async (elementId) => {
    try {
      await api.delete(`/offer-elements/${elementId}`);

      // Reload the saved elements list with current filters
      const reloadParams = {};
      if (selectedBrand) reloadParams.brandId = selectedBrand;
      if (selectedOffer) reloadParams.offerId = selectedOffer;
      const reloadResponse = await api.get('/offer-elements', { params: reloadParams });
      setFilteredSavedElements(reloadResponse.data);
      setSavedOfferElements(reloadResponse.data);
    } catch (error) {
      console.error('Error deleting saved offer element:', error);
    }
  };

  const renameSavedElement = async (elementId, newName) => {
    if (!newName.trim()) {
      setStatusTitle('Missing Information');
      setStatusMessage('Please enter a valid name');
      setShowStatusAlert(true);
      return;
    }

    try {
      await api.put(`/offer-elements/${elementId}`, { name: newName });

      // Reload the saved elements list with current filters
      const reloadParams = {};
      if (selectedBrand) reloadParams.brandId = selectedBrand;
      if (selectedOffer) reloadParams.offerId = selectedOffer;
      const reloadResponse = await api.get('/offer-elements', { params: reloadParams });
      setFilteredSavedElements(reloadResponse.data);
      setSavedOfferElements(reloadResponse.data);

      // If the renamed element is currently being edited, update the currentElementName
      if (selectedSavedElementId === elementId) {
        setCurrentElementName(newName);
      }

      setStatusTitle('Success');
      setStatusMessage(`Renamed to "${newName}" successfully!`);
      setShowStatusAlert(true);
    } catch (error) {
      console.error('Error renaming saved offer element:', error);
      setStatusTitle('Error');
      setStatusMessage('Failed to rename element. Please try again.');
      setShowStatusAlert(true);
    }
  };

  const editSavedElement = (savedElement) => {
    setCurrentElementName(savedElement.name);
    setSelectedSavedElementId(savedElement._id);
    setIsUpdatingElement(true);
    setShowSaveElementDialog(true);

    // Set brand and offer if they exist in the saved element
    if (savedElement.brandId) {
      setSelectedBrand(savedElement.brandId);
      if (savedElement.offerId) {
        setSelectedOffer(savedElement.offerId);
        // Load offers for the brand if not already loaded
        if (!offers.find(o => o._id === savedElement.offerId)) {
          loadOffersForBrand(savedElement.brandId);
        }
      }
    }
  };

  const loadSavedOfferElements = useCallback(async (brandFilter = null, offerFilter = null) => {
    try {
      console.log('📥 Loading saved offer elements with filters:', { brandFilter, offerFilter });

      // Build query parameters
      const params = {};
      if (brandFilter) params.brandId = brandFilter;
      if (offerFilter) params.offerId = offerFilter;

      console.log('📤 Client sending request with params:', params);

      const response = await api.get('/offer-elements', { params });
      console.log('✅ Loaded elements:', response.data);
      console.log('✅ Setting filtered elements to:', response.data.length, 'elements');
      setSavedOfferElements(response.data);
      setFilteredSavedElements(response.data);
    } catch (error) {
      console.error('❌ Error loading saved offer elements:', error);
      console.error('Error details:', error.response?.data || error.message);
    }
  }, []);

  return {
    saveCurrentElement,
    loadSavedElement,
    deleteSavedElement,
    renameSavedElement,
    editSavedElement,
    loadSavedOfferElements,
  };
};
